import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    X, CheckCircle2, User, Building, ShieldCheck, FileText, Upload, 
    Image as ImageIcon, Loader2, CreditCard, Calendar, Mail, Phone, 
    Briefcase, Tag, AlertTriangle, ArrowRight, ArrowLeft, Info, XCircle, Copy, Check
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';
import EmployeeAccessStep from './EmployeeAccessStep';
import CustomDatePicker from './common/CustomDatePicker';
import { turkeyCities } from '../utils/turkeyCities';

const scopeStringToValue = {
    DepartmentAndLocation: 5,
    Location: 4,
    AllData: 3,
    Department: 2,
    OwnData: 1,
    None: 0,
};

const normalizeScopeValue = (v) => {
    if (typeof v === 'number') return (v >= 0 && v <= 5) ? v : 1;
    if (typeof v === 'string') return scopeStringToValue[v] ?? 1;
    return 1;
};

export default function PersonnelOnboardingWizard({ onClose, onSuccess }) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [dropdownsLoading, setDropdownsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);
    const fileInputRef = useRef(null);
    const contentRef = useRef(null);

    // Dropdowns
    const [departments, setDepartments] = useState([]);
    const [positions, setPositions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [locations, setLocations] = useState([]);
    const [workingRegimes, setWorkingRegimes] = useState([]);

    // Data State
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', sicilNo: '', tcNo: '', 
        dateOfBirth: '', gender: 'Male',
        departmentId: '', positionId: '', locationId: '', 
        hireDate: '', terminationDate: '', terminationReason: '', userRole: 'Personel', seniorityDate: '', previousLiquidatedYears: '',
        phoneNumber: '', email: '', workingRegimeId: '',
        // Adım 4 detayları:
        bloodType: '', cityId: '', districtName: '', neighborhood: '', 
        addressLine: '', postalCode: '', emergencyContactName: '', 
        emergencyContactPhone: '', notes: ''
    });
    const [dataScope, setDataScope] = useState({ dataAccessType: 1, departmentIds: [], locationIds: [] });
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [showAdvancedSeniority, setShowAdvancedSeniority] = useState(false);

    // Results from Core Onboarding
    const [createdEmployeeId, setCreatedEmployeeId] = useState(null);
    const [createdSicilNo, setCreatedSicilNo] = useState(null);
    const [createdUsername, setCreatedUsername] = useState(null);
    const [temporaryPassword, setTemporaryPassword] = useState(null);
    const [isSuccessFinished, setIsSuccessFinished] = useState(false);
    const [copiedSummary, setCopiedSummary] = useState(false);

    useEffect(() => {
        if (error || warning) {
            contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [error, warning]);

    useEffect(() => {
        const fetchDropdowns = async () => {
            setDropdownsLoading(true);
            try {
                const [deptData, posData, rolesData, locData, wrData] = await Promise.all([
                    apiClient('Department'),
                    apiClient('Position'),
                    apiClient('roles/dropdown').catch(() => []), // roles endpoint
                    apiClient('Location').catch(() => []),
                    apiClient('WorkingRegime').catch(() => [])
                ]);
                setDepartments(deptData || []);
                setPositions(posData || []);
                setRoles(rolesData || []);
                setLocations(locData || []);
                setWorkingRegimes(wrData || []);
            } catch (err) {
                console.error("Dropdown yükleme hatası", err);
            } finally {
                setDropdownsLoading(false);
            }
        };
        fetchDropdowns();
    }, []);

    const toLocalISOString = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const tzOffset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzOffset).toISOString().slice(0, 10);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'tcNo') {
            const v = value.replace(/\D/g, '');
            if (v.length <= 11) setFormData(p => ({ ...p, [name]: v }));
        } else if (name === 'phoneNumber') {
            let v = value.replace(/\D/g, '');
            if (v.startsWith('0')) v = v.substring(1);
            if (v.startsWith('90')) v = v.substring(2);
            if (v.length > 10) v = v.slice(0, 10);
            setFormData(p => ({ ...p, [name]: v }));
        } else {
            setFormData(p => ({ ...p, [name]: value }));
        }
    };

    const handleDateChange = (dateString, name) => {
        setFormData(prev => ({ ...prev, [name]: dateString }));
    };

    const handleDataScopeTypeChange = (e) => {
        const type = Number(e.target.value);
        setDataScope(prev => ({
            ...prev,
            dataAccessType: type,
            departmentIds: (type === 2 || type === 5) ? prev.departmentIds : [],
            locationIds: (type === 4 || type === 5) ? prev.locationIds : []
        }));
    };

    const handleDepartmentCheckboxChange = (departmentId) => {
        setDataScope(prev => {
            const s = new Set(prev.departmentIds);
            s.has(departmentId) ? s.delete(departmentId) : s.add(departmentId);
            return { ...prev, departmentIds: Array.from(s) };
        });
    };

    const handleLocationCheckboxChange = (locationId) => {
        setDataScope(prev => {
            const s = new Set(prev.locationIds);
            s.has(locationId) ? s.delete(locationId) : s.add(locationId);
            return { ...prev, locationIds: Array.from(s) };
        });
    };

    const formatPhoneNumberDisplay = (num) => {
        if (!num) return '';
        let clean = String(num).replace(/\D/g, '');
        if (clean.length === 0) return '';
        if (clean.length < 4) return `0 (${clean}`;
        if (clean.length < 7) return `0 (${clean.slice(0, 3)}) ${clean.slice(3)}`;
        if (clean.length < 9) return `0 (${clean.slice(0, 3)}) ${clean.slice(3, 6)} ${clean.slice(6)}`;
        return `0 (${clean.slice(0, 3)}) ${clean.slice(3, 6)} ${clean.slice(6, 8)} ${clean.slice(8, 10)}`;
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError("Fotoğraf dosya boyutu 5 MB'ı aşamaz.");
                return;
            }
            setError(null);
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const validateStep1 = () => {
        if (!formData.firstName.trim()) return 'Ad alanı zorunludur.';
        if (!formData.lastName.trim()) return 'Soyad alanı zorunludur.';
        if (!formData.tcNo.trim() || formData.tcNo.length !== 11 || !/^\d{11}$/.test(formData.tcNo)) return 'T.C. Kimlik No 11 haneli rakam olmalıdır.';
        if (!formData.dateOfBirth) return 'Doğum tarihi zorunludur.';
        return null;
    };

    const validateStep2 = () => {
        if (!formData.departmentId) return 'Departman seçimi zorunludur.';
        if (!formData.positionId) return 'Pozisyon seçimi zorunludur.';
        if (!formData.hireDate) return 'İşe giriş tarihi zorunludur.';
        if (formData.dateOfBirth && formData.hireDate) {
            const dob = new Date(formData.dateOfBirth);
            const hire = new Date(formData.hireDate);
            const minHire = new Date(dob);
            minHire.setFullYear(minHire.getFullYear() + 15);
            if (hire < minHire) {
                return 'İşe giriş tarihi, doğum tarihinden en az 15 yıl sonra olmalıdır.';
            }
        }
        if (formData.hireDate && formData.terminationDate) {
            const hire = new Date(formData.hireDate);
            const term = new Date(formData.terminationDate);
            if (term < hire) {
                return 'İşten ayrılış tarihi, işe giriş tarihinden önce olamaz.';
            }
        }
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) return 'Geçerli bir e-posta adresi giriniz.';
        if (formData.phoneNumber && !/^0?5[0-9]{9}$/.test(String(formData.phoneNumber).replace(/\D/g, ''))) return 'Geçerli bir cep telefonu giriniz (05XX XXX XX XX).';
        if ((dataScope.dataAccessType === 2 || dataScope.dataAccessType === 5) && (!dataScope.departmentIds || dataScope.departmentIds.length === 0)) {
            return 'Bölüm bazlı veri görme kapsamında en az 1 bölüm seçmelisiniz.';
        }
        if ((dataScope.dataAccessType === 4 || dataScope.dataAccessType === 5) && (!dataScope.locationIds || dataScope.locationIds.length === 0)) {
            return 'Lokasyon bazlı veri görme kapsamında en az 1 lokasyon seçmelisiniz.';
        }
        return null;
    };

    const goToNextStep = async () => {
        setError(null);
        if (step === 1) {
            const err = validateStep1();
            if (err) { setError(err); return; }
            setStep(2);
        } else if (step === 2) {
            const err = validateStep2();
            if (err) { setError(err); return; }
            
            // Submitting Core Data
            setLoading(true);
            try {
                const payload = {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    turkishIdNo: formData.tcNo,
                    sicilNo: formData.sicilNo || null,
                    dateOfBirth: toLocalISOString(formData.dateOfBirth),
                    gender: formData.gender,
                    departmentId: Number(formData.departmentId),
                    positionId: Number(formData.positionId),
                    locationId: formData.locationId ? Number(formData.locationId) : null,
                    hireDate: toLocalISOString(formData.hireDate),
                    terminationDate: toLocalISOString(formData.terminationDate),
                    terminationReason: formData.terminationReason || null,
                    seniorityDate: showAdvancedSeniority ? toLocalISOString(formData.seniorityDate) : null,
                    previousLiquidatedYears: showAdvancedSeniority && formData.previousLiquidatedYears ? Number(formData.previousLiquidatedYears) : null,
                    userRole: formData.userRole,
                    dataAccessType: dataScope.dataAccessType,
                    phoneNumber: formData.phoneNumber || null,
                    email: formData.email || null,
                    workingRegimeId: formData.workingRegimeId ? Number(formData.workingRegimeId) : null
                };

                const result = await apiClient('Employee/OnboardCore', {
                    method: 'POST',
                    body: payload
                });

                const newEmpId = result.employeeId;
                setCreatedEmployeeId(newEmpId);
                setCreatedSicilNo(result.sicilNo);
                setCreatedUsername(result.username || formData.tcNo);
                setTemporaryPassword(result.temporaryPassword || formData.tcNo);

                if (newEmpId) {
                    try {
                        await apiClient(`Employee/${newEmpId}/datascope`, {
                            method: 'PUT',
                            body: {
                                dataAccessType: dataScope.dataAccessType,
                                departmentIds: dataScope.departmentIds || [],
                                locationIds: dataScope.locationIds || []
                            }
                        });
                    } catch (scopeErr) {
                        console.warn('Onboarding datascope PUT hatası:', scopeErr);
                    }
                }

                // Upload Photo if exists
                if (photoFile && newEmpId) {
                    try {
                        const formDataPhoto = new FormData();
                        formDataPhoto.append('file', photoFile);
                        await apiClient(`EmployeeProfileMedia/${newEmpId}/upload`, {
                            method: 'POST',
                            body: formDataPhoto,
                            headers: { 'Accept': 'application/json' },
                            isFormData: true
                        });
                    } catch (photoErr) {
                        console.error("Fotoğraf yükleme hatası", photoErr);
                        setWarning(`Personel eklendi ancak fotoğraf yüklenemedi: ${photoErr.message}`);
                    }
                }

                setStep(3); // Advance to Optional Step 3
            } catch (err) {
                setError(err.message || 'Kayıt sırasında bir hata oluştu.');
            } finally {
                setLoading(false);
            }
        } else if (step === 3) {
            setStep(4);
        } else if (step === 4) {
            setIsSuccessFinished(true);
        }
    };

    const goToPrevStep = () => {
        setError(null);
        setWarning(null);
        if (step > 1 && step < 3) setStep(step - 1);
    };

    const renderStepIndicators = () => {
        const steps = [
            { id: 1, title: '1. Temel Bilgiler', icon: User },
            { id: 2, title: '2. Kurumsal & Yetki', icon: Building },
            { id: 3, title: '3. Geçiş Kontrol', icon: ShieldCheck },
            { id: 4, title: '4. İK Detayları', icon: FileText }
        ];

        return (
            <div className="inline-flex flex-wrap items-center gap-1.5 p-1 rounded-xl bg-gray-200/70 dark:bg-gray-800 border border-gray-300/60 dark:border-gray-700">
                {steps.map((s) => {
                    const isActive = step === s.id;
                    const isCompleted = step > s.id;

                    return (
                        <div
                            key={s.id}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                isActive
                                    ? 'bg-white dark:bg-indigo-600 text-indigo-700 dark:text-white shadow-sm'
                                    : isCompleted
                                        ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50/70 dark:bg-emerald-900/20'
                                        : 'text-gray-500 dark:text-gray-400'
                            }`}
                        >
                            {isCompleted ? <CheckCircle2 size={14} className="text-emerald-500" /> : <s.icon size={14} />}
                            <span>{s.title}</span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderStep1 = () => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pb-4">
            <div className="md:col-span-2 bg-gray-50/80 dark:bg-gray-800/50 p-5 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-1.5">
                    <User size={14} /> Kimlik Bilgileri
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Ad *</label>
                        <input type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Soyad *</label>
                        <input type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all" />
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">T.C. Kimlik No *</label>
                        <input type="text" name="tcNo" value={formData.tcNo} onChange={handleChange} maxLength="11"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all font-mono" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center justify-between">
                            Sicil No
                            <span className="text-[11px] font-normal text-gray-400 flex items-center">
                                <Info size={11} className="mr-1"/> Boşsa otomatik üretilir
                            </span>
                        </label>
                        <input type="text" name="sicilNo" value={formData.sicilNo} onChange={handleChange} placeholder="Örn: 1045"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all font-mono" />
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Doğum Tarihi *</label>
                        <CustomDatePicker selectedDate={formData.dateOfBirth} onDateChange={(d) => handleDateChange(d, 'dateOfBirth')} placeholder="GG.AA.YYYY" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cinsiyet *</label>
                        <select name="gender" value={formData.gender} onChange={handleChange}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all">
                            <option value="Male">Erkek</option>
                            <option value="Female">Kadın</option>
                        </select>
                    </div>
                </div>
            </div>
            
            <div className="md:col-span-1 flex flex-col items-center justify-center p-5 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50/80 dark:bg-gray-800/50">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Profil Fotoğrafı (Opsiyonel)</p>
                <div 
                    className="w-28 h-28 rounded-full overflow-hidden bg-white dark:bg-gray-700 border-4 border-white dark:border-gray-600 shadow-md flex items-center justify-center cursor-pointer relative group"
                    onClick={() => fileInputRef.current?.click()}
                >
                    {photoPreview ? (
                        <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                        <User className="w-12 h-12 text-gray-300 dark:text-gray-500" />
                    )}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Upload className="w-6 h-6 text-white" />
                    </div>
                </div>
                <input type="file" ref={fileInputRef} onChange={handlePhotoChange} accept="image/jpeg, image/png, image/webp" className="hidden" />
                <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-3 text-xs text-indigo-600 dark:text-indigo-400 font-semibold hover:underline">
                    Fotoğraf {photoPreview ? 'Değiştir' : 'Seç'}
                </button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-4 pb-4">
            {dropdownsLoading && (
                <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60 z-10 flex items-center justify-center backdrop-blur-sm rounded-xl">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3.5">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2">Kurumsal Atama</h4>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bölüm *</label>
                        <select name="departmentId" value={formData.departmentId} onChange={handleChange}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all">
                            <option value="">Seçiniz</option>
                            {departments.filter(d => d.isActive !== false).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Pozisyon *</label>
                        <select name="positionId" value={formData.positionId} onChange={handleChange}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all">
                            <option value="">Seçiniz</option>
                            {positions.filter(p => p.isActive !== false).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Lokasyon</label>
                        <select name="locationId" value={formData.locationId} onChange={handleChange}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all">
                            <option value="">Seçiniz</option>
                            {locations.filter(l => l.isActive !== false).map(l => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3.5">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2">İletişim &amp; Çalışma</h4>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Telefon Numarası</label>
                        <input type="tel" name="phoneNumber" value={formatPhoneNumberDisplay(formData.phoneNumber)} onChange={handleChange} placeholder="0 (5XX) XXX XX XX"
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">E-Posta Adresi (Opsiyonel)</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="ornek@sirket.com"
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all" />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Çalışma Rejimi</label>
                        <select name="workingRegimeId" value={formData.workingRegimeId} onChange={handleChange}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all">
                            <option value="">Seçiniz</option>
                            {workingRegimes.filter(w => w.isActive !== false).map(w => (
                                <option key={w.id} value={w.id}>
                                    {w.name}{w.defaultWorkPeriodName ? ` (${w.defaultWorkPeriodName})` : ''}
                                </option>
                            ))}
                        </select>
                        {(() => {
                            const selectedWr = workingRegimes.find(w => String(w.id) === String(formData.workingRegimeId));
                            if (!selectedWr?.defaultWorkPeriodName) return null;
                            return (
                                <p className="mt-1.5 text-[11px] text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 px-2.5 py-1.5 rounded-lg">
                                    İşe giriş tarihinden itibaren otomatik olarak <strong>{selectedWr.defaultWorkPeriodName}</strong> vardiyası tanımlanacaktır.
                                </p>
                            );
                        })()}
                    </div>
                </div>

                <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3.5">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2">Sistem &amp; Yetki</h4>
                    <div className="grid grid-cols-2 gap-2.5">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İşe Giriş *</label>
                            <CustomDatePicker selectedDate={formData.hireDate} onDateChange={(d) => handleDateChange(d, 'hireDate')} placeholder="GG.AA.YYYY" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İşten Çıkış</label>
                            <CustomDatePicker selectedDate={formData.terminationDate} onDateChange={(d) => handleDateChange(d, 'terminationDate')} placeholder="GG.AA.YYYY" />
                        </div>
                    </div>
                    {formData.terminationDate && (
                        <div className="animate-in fade-in duration-200">
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Çıkış Nedeni (Opsiyonel)</label>
                            <input type="text" name="terminationReason" value={formData.terminationReason} onChange={handleChange} placeholder="Örn: İstifa, Sözleşme Bitimi"
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all" />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2.5">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kullanıcı Rolü *</label>
                            <select name="userRole" value={formData.userRole} onChange={handleChange}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all">
                                {roles.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
                                {roles.length === 0 && <option value="Personel">Personel</option>}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Veri Görme Kapsamı</label>
                            <select name="dataAccessType" value={dataScope.dataAccessType} onChange={handleDataScopeTypeChange}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all">
                                <option value={1}>Kendi Verileri</option>
                                <option value={2}>Bölüm Bazlı</option>
                                <option value={4}>Lokasyon Bazlı</option>
                                <option value={5}>Bölüm + Lokasyon</option>
                                <option value={3}>Tüm Veriler</option>
                            </select>
                        </div>
                    </div>

                    {(dataScope.dataAccessType === 2 || dataScope.dataAccessType === 4 || dataScope.dataAccessType === 5) && (
                        <div className={`grid gap-2 ${dataScope.dataAccessType === 5 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            {(dataScope.dataAccessType === 2 || dataScope.dataAccessType === 5) && (
                                <div className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] font-semibold text-gray-500 uppercase">Bölüm ({dataScope.departmentIds.length})</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const activeIds = departments.filter(d => d.isActive !== false).map(d => d.id);
                                                const allSelected = activeIds.length > 0 && activeIds.every(id => dataScope.departmentIds.includes(id));
                                                setDataScope(prev => ({ ...prev, departmentIds: allSelected ? [] : activeIds }));
                                            }}
                                            className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                                        >
                                            Tümü
                                        </button>
                                    </div>
                                    <div className="max-h-24 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                        {departments.filter(d => d.isActive !== false).map(dep => (
                                            <label key={dep.id} className="flex items-center p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={dataScope.departmentIds.includes(dep.id)}
                                                    onChange={() => handleDepartmentCheckboxChange(dep.id)}
                                                    className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 shrink-0"
                                                />
                                                <span className="ml-2 text-xs text-gray-800 dark:text-gray-200 truncate">{dep.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {(dataScope.dataAccessType === 4 || dataScope.dataAccessType === 5) && (
                                <div className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] font-semibold text-gray-500 uppercase">Lokasyon ({dataScope.locationIds.length})</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const activeIds = locations.filter(l => l.isActive !== false).map(l => l.id);
                                                const allSelected = activeIds.length > 0 && activeIds.every(id => dataScope.locationIds.includes(id));
                                                setDataScope(prev => ({ ...prev, locationIds: allSelected ? [] : activeIds }));
                                            }}
                                            className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                                        >
                                            Tümü
                                        </button>
                                    </div>
                                    <div className="max-h-24 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                                        {locations.filter(l => l.isActive !== false).map(loc => (
                                            <label key={loc.id} className="flex items-center p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={dataScope.locationIds.includes(loc.id)}
                                                    onChange={() => handleLocationCheckboxChange(loc.id)}
                                                    className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 shrink-0"
                                                />
                                                <span className="ml-2 text-xs text-gray-800 dark:text-gray-200 truncate">{loc.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="pt-2 border-t border-gray-200/80 dark:border-gray-700">
                        <div className="flex items-center">
                            <input id="showAdvancedSeniorityWizard" type="checkbox" checked={showAdvancedSeniority} onChange={(e) => setShowAdvancedSeniority(e.target.checked)}
                                className="h-3.5 w-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 bg-white dark:bg-gray-700" />
                            <label htmlFor="showAdvancedSeniorityWizard" className="ml-2 block text-xs font-medium text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                                Özel Kıdem Ayarları (Geçmiş Kıdem / Devir)
                            </label>
                        </div>
                        
                        {showAdvancedSeniority && (
                            <div className="mt-2 p-2.5 bg-indigo-50/70 dark:bg-indigo-900/15 rounded-lg border border-indigo-100 dark:border-indigo-800/30 grid grid-cols-2 gap-2.5 animate-in fade-in duration-200">
                                <div>
                                    <label className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1">Kıdem Başlangıç</label>
                                    <CustomDatePicker selectedDate={formData.seniorityDate} onDateChange={(d) => handleDateChange(d, 'seniorityDate')} placeholder="GG.AA.YYYY" />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-medium text-gray-700 dark:text-gray-300 mb-1">Geçmiş Kıdem (Yıl)</label>
                                    <input type="number" name="previousLiquidatedYears" value={formData.previousLiquidatedYears} onChange={handleChange} placeholder="Örn: 3" min="0" max="50"
                                        className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white transition-all" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            <div className="bg-blue-50/80 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 px-3.5 py-2.5 rounded-xl flex items-center text-xs border border-blue-200/60 dark:border-blue-800/40">
                <Info size={16} className="mr-2 flex-shrink-0" />
                <span>
                    Bu adımı tamamladığınızda personel kalıcı olarak sisteme kaydedilir. Sonraki adımlar (Kimlik Kartı ve İK Detayları) opsiyoneldir.
                </span>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700 min-h-[380px]">
            <EmployeeAccessStep 
                employee={{ id: createdEmployeeId, firstName: formData.firstName, lastName: formData.lastName }} 
                wasJustDeactivated={false}
            />
        </div>
    );

    const renderStep4 = () => {
        const selectedCity = turkeyCities.find(c => c.id === Number(formData.cityId));
        const availableDistricts = selectedCity ? selectedCity.districts : [];

        return (
            <div className="space-y-4">
                <div className="bg-indigo-50/80 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-300 px-4 py-2.5 rounded-xl flex items-center border border-indigo-200/60 dark:border-indigo-800/50 text-xs">
                    <Info size={16} className="mr-2.5 flex-shrink-0" />
                    <span>
                        <strong>Ek İK Detayları (Opsiyonel):</strong> Aşağıdaki alanlar personelin detaylı insan kaynakları dosyasını oluşturur. Dilerseniz boş bırakarak kaydı tamamlayabilirsiniz.
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                    {/* 1. Kolon: Kişisel & Acil Durum */}
                    <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2">Sağlık &amp; Acil Durum</h4>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Kan Grubu</label>
                            <select name="bloodType" value={formData.bloodType} onChange={handleChange}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white">
                                <option value="">Seçiniz</option>
                                <option value="A Rh+">A Rh+</option>
                                <option value="A Rh-">A Rh-</option>
                                <option value="B Rh+">B Rh+</option>
                                <option value="B Rh-">B Rh-</option>
                                <option value="AB Rh+">AB Rh+</option>
                                <option value="AB Rh-">AB Rh-</option>
                                <option value="0 Rh+">0 Rh+</option>
                                <option value="0 Rh-">0 Rh-</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Acil Durum Kişisi</label>
                            <input type="text" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white" placeholder="İsim Soyisim" />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Acil Durum Telefonu</label>
                            <input type="text" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleChange}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white" placeholder="05XX XXX XX XX" />
                        </div>
                    </div>

                    {/* 2. Kolon: İletişim & Adres */}
                    <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2">İletişim &amp; Adres</h4>
                        <div className="grid grid-cols-2 gap-2.5">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İl</label>
                                <select name="cityId" value={formData.cityId} onChange={(e) => { handleChange(e); setFormData(p => ({...p, districtName: ''})) }}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white">
                                    <option value="">İl Seçiniz</option>
                                    {turkeyCities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">İlçe</label>
                                <select name="districtName" value={formData.districtName} onChange={handleChange} disabled={!formData.cityId}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white disabled:opacity-50">
                                    <option value="">İlçe Seçiniz</option>
                                    {availableDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2.5">
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Mahalle</label>
                                <input type="text" name="neighborhood" value={formData.neighborhood} onChange={handleChange}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white" placeholder="Mahalle Adı" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">P. Kodu</label>
                                <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange}
                                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white" placeholder="34000" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Açık Adres</label>
                            <input type="text" name="addressLine" value={formData.addressLine} onChange={handleChange}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white" placeholder="Sokak, Apartman, Kapı No vb." />
                        </div>
                    </div>

                    {/* 3. Kolon: Ek Notlar */}
                    <div className="bg-gray-50/80 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700 space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border-b border-gray-200 dark:border-gray-700 pb-2">Personel Notu</h4>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama / Notlar</label>
                            <textarea rows={6} name="notes" value={formData.notes} onChange={handleChange}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white resize-none" placeholder="Personel hakkında ek İK notları..."></textarea>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleFinishAndClose = () => {
        if (createdEmployeeId && onSuccess) {
            onSuccess();
        } else if (onClose) {
            onClose();
        }
    };

    const handleFinalSave = async () => {
        try {
            setLoading(true);
            setError(null);
            
            await apiClient(`Employee/${createdEmployeeId}/details`, {
                method: 'PUT',
                body: {
                    gender: formData.gender,
                    locationId: formData.locationId ? Number(formData.locationId) : null,
                    workingRegimeId: formData.workingRegimeId ? Number(formData.workingRegimeId) : null,
                    bloodType: formData.bloodType || null,
                    cityId: formData.cityId ? Number(formData.cityId) : null,
                    districtName: formData.districtName || null,
                    neighborhood: formData.neighborhood || null,
                    addressLine: formData.addressLine || null,
                    postalCode: formData.postalCode || null,
                    emergencyContactName: formData.emergencyContactName || null,
                    emergencyContactPhone: formData.emergencyContactPhone || null,
                    notes: formData.notes || null
                }
            });
            
            setIsSuccessFinished(true);
        } catch (err) {
            setError(err.message || 'Detaylar kaydedilirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    const renderSuccess = () => {
        const effectiveUsername = createdUsername || formData.tcNo;
        const isSicilLogin = effectiveUsername === createdSicilNo && createdSicilNo !== formData.tcNo;

        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                <div className="bg-white dark:bg-gray-900 w-full max-w-xl rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 flex flex-col items-center justify-center py-10 px-6 text-center">
                    <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mb-5">
                        <CheckCircle2 size={42} className="text-emerald-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Personel Başarıyla Oluşturuldu!</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300 max-w-md mx-auto mb-6">
                        <strong>{formData.firstName} {formData.lastName}</strong> isimli personelin kayıt, yetki ve kullanıcı hesabı işlemleri tamamlanmıştır.
                    </p>

                    <div className="bg-gray-50 dark:bg-gray-800/80 p-5 rounded-2xl w-full border border-gray-200 dark:border-gray-700 space-y-4">
                        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3.5">
                            <div className="text-left">
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider block mb-0.5">Sicil Numarası</span>
                                <span className="font-mono text-lg font-bold text-gray-900 dark:text-white">{createdSicilNo}</span>
                            </div>
                            <div className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-3 py-1 rounded-full text-xs font-semibold">
                                {formData.sicilNo ? 'Manuel Sicil' : 'Sistem Üretimi'}
                            </div>
                        </div>
                        
                        <div className="text-left pt-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wider block mb-2.5">Sisteme Giriş Bilgileri</span>
                            <div className="space-y-2.5">
                                <div className="flex justify-between items-center bg-white dark:bg-gray-900 p-3 rounded-xl border border-gray-200/70 dark:border-gray-800 shadow-sm">
                                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                        Kullanıcı Adı ({isSicilLogin ? 'Sicil No' : 'T.C. Kimlik No'})
                                    </span>
                                    <span className="font-mono text-sm font-bold text-indigo-600 dark:text-indigo-400">{effectiveUsername}</span>
                                </div>
                                <div className="flex justify-between items-center bg-amber-50 dark:bg-amber-900/15 p-3 rounded-xl border border-amber-200/80 dark:border-amber-800/60 shadow-sm">
                                    <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">İlk Giriş Şifresi (T.C. No)</span>
                                    <span className="font-mono text-sm font-bold text-amber-800 dark:text-amber-300">{temporaryPassword || formData.tcNo}</span>
                                </div>
                                <div className="text-xs text-left text-emerald-700 dark:text-emerald-300 bg-emerald-50/80 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/40 p-2.5 rounded-xl flex items-center gap-2">
                                    <ShieldCheck size={16} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
                                    <span>Güvenlik gereği personel ilk giriş yaptığında şifresini değiştirmek zorundadır.</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                const text = `Personel: ${formData.firstName} ${formData.lastName}\nSicil No: ${createdSicilNo}\nKullanıcı Adı: ${effectiveUsername}\nİlk Giriş Şifresi: ${temporaryPassword || formData.tcNo}`;
                                navigator.clipboard?.writeText(text);
                                setCopiedSummary(true);
                                setTimeout(() => setCopiedSummary(false), 2500);
                            }}
                            className="inline-flex items-center gap-2 px-5 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold rounded-xl border border-gray-200 dark:border-gray-700 transition-colors"
                        >
                            {copiedSummary ? <Check size={18} className="text-emerald-500" /> : <Copy size={18} />}
                            {copiedSummary ? 'Bilgiler Kopyalandı' : 'Giriş Bilgilerini Kopyala'}
                        </button>
                        <button 
                            onClick={handleFinishAndClose} 
                            className="px-8 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-lg transition-colors"
                        >
                            Panele Dön
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (isSuccessFinished) return renderSuccess();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-5 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 w-full max-w-6xl rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 flex flex-col max-h-[94vh] overflow-hidden">
                {/* Kompakt Başlık + Yatay Adım Göstergeleri */}
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex flex-wrap justify-between items-center gap-3 bg-gray-50/70 dark:bg-gray-800/40 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                            <User size={20} />
                        </div>
                        <div>
                            <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                Yeni Personel Ekle
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Sisteme yeni personel eklemek için adımları takip edin.</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {renderStepIndicators()}
                        <button onClick={handleFinishAndClose} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/70 dark:hover:bg-gray-800 rounded-full transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div ref={contentRef} className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar relative">
                    {error && (
                        <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 p-3 rounded-xl border border-red-100 dark:border-red-900/50 flex items-start">
                            <XCircle size={18} className="mr-2.5 flex-shrink-0 mt-0.5" />
                            <p className="text-sm font-medium">{error}</p>
                        </div>
                    )}
                    {warning && (
                        <div className="mb-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 p-3 rounded-xl border border-yellow-100 dark:border-yellow-900/50 flex items-start">
                            <AlertTriangle size={18} className="mr-2.5 flex-shrink-0 mt-0.5" />
                            <p className="text-sm font-medium">{warning}</p>
                        </div>
                    )}

                    <div className="animate-in slide-in-from-right-4 fade-in duration-300">
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                        {step === 4 && renderStep4()}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center shrink-0">
                    <div>
                        {step > 1 && step < 3 && (
                            <button onClick={goToPrevStep} disabled={loading} className="px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium flex items-center transition-colors">
                                <ArrowLeft size={18} className="mr-2" /> Geri
                            </button>
                        )}
                    </div>
                    
                    <div className="flex space-x-3">
                        {step >= 3 && step < 4 && (
                            <button onClick={() => setStep(step + 1)} className="px-5 py-2.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 font-medium transition-colors">
                                Bu Adımı Atla
                            </button>
                        )}
                        {step === 4 && (
                            <button onClick={handleFinalSave} disabled={loading} className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium shadow-sm transition-colors flex items-center disabled:opacity-50">
                                {loading && <Loader2 size={18} className="animate-spin mr-2" />}
                                Tamamla
                                {!loading && <CheckCircle2 size={18} className="ml-2" />}
                            </button>
                        )}
                        {step < 4 && (
                            <button onClick={goToNextStep} disabled={loading} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium shadow-sm transition-colors flex items-center disabled:opacity-70 disabled:cursor-not-allowed">
                                {loading && <Loader2 size={18} className="animate-spin mr-2" />}
                                {step === 2 ? 'Kaydet ve Devam Et' : 'İleri'}
                                {!loading && step !== 2 && <ArrowRight size={18} className="ml-2" />}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


